import {
  CUSTOM_QUERY_MANAGER_CATEGORY,
  CUSTOM_QUERY_MANAGER_CATEGORY_FOR_API,
  CUSTOM_STAT_PREFIX,
} from '@/common/utils/statInfo.define';
import { type WidgetChartType } from '@/dashboard/shared/types/widgets.types';
import { StatDataType, type StatInfo } from '@/common/components/organisms/statWindow';
import { validStatByChartType } from '@/dashboard/features/widgets/common/constants/define';
import { getDetailKey } from '@/common/utils/string.utils';
import { StatCategory } from './statCategoryList';
import { ChildCategory, ParentCategory } from './statContents.setup';

export const HIDDEN_STAT_DATA_TYPES: StatDataType[] = ['topology', 'cluster'];

export const sortParentCategory = (category1: string, category2: string) => {
  if (category1 === 'custom' || category1.startsWith(CUSTOM_STAT_PREFIX)) {
    return 1;
  }
  if (category2 === 'custom' || category2.startsWith(CUSTOM_STAT_PREFIX)) {
    return -1;
  }
  return category1.localeCompare(category2);
};

export const sortCategory = (category1: string, category2: string) => {
  if (category1 === 'common') {
    return -1;
  }
  if (category2 === 'common') {
    return 1;
  }
  return category1.localeCompare(category2);
};

export const sortTag = (tag1: string, tag2: string) => {
  return tag1.localeCompare(tag2);
};

export const mapChildCategoryToExact = ({ parentCategory, childCategory }: StatCategory) => {
  if (parentCategory === childCategory) {
    return 'common';
  }
  return childCategory;
};

// UI 표시용: mapChildCategoryToExact + CUSTOM_QUERY_MANAGER 예외 처리
export const mapChildCategoryToDisplay = (
  parentCategory: string,
  childCategory: string,
): string => {
  if (
    parentCategory.startsWith(CUSTOM_QUERY_MANAGER_CATEGORY) &&
    childCategory === CUSTOM_QUERY_MANAGER_CATEGORY_FOR_API
  ) {
    return 'common';
  }
  return mapChildCategoryToExact({ parentCategory, childCategory });
};

export const mapChildCategoryWithParent = ({ parentCategory, childCategory }: StatCategory) => {
  if (parentCategory.startsWith(CUSTOM_QUERY_MANAGER_CATEGORY)) {
    return CUSTOM_QUERY_MANAGER_CATEGORY_FOR_API;
  }
  return childCategory === 'common' ? parentCategory : childCategory;
};

export const setFilterStatList = ({
  statInfoList,
  tags = [],
  filterByFavorite = false,
  widgetChartType,
}: {
  statInfoList: StatInfo[];
  filterByFavorite?: boolean;
  tags?: string[];
  widgetChartType?: WidgetChartType;
}) => {
  return statInfoList.filter(({ tagList: statTagList, favorite, id }) => {
    const idMatching = validStatByChartType[widgetChartType ?? '']?.includes(id) ?? true;
    const matchesTags = tags?.some((tag) => statTagList.includes(tag));

    if (!tags?.length) {
      if (filterByFavorite) return favorite && idMatching;
      if (widgetChartType && Object.keys(validStatByChartType).includes(widgetChartType))
        return idMatching;
      return true;
    }

    if (widgetChartType && Object.keys(validStatByChartType).includes(widgetChartType)) {
      return filterByFavorite ? favorite && idMatching && matchesTags : idMatching && matchesTags;
    }

    return filterByFavorite ? favorite && matchesTags : matchesTags;
  });
};

const filterStatBySearchWord = (statInfos: StatInfo[], searchWord: string) => {
  if (!searchWord.trim()) {
    return statInfos.slice();
  }
  const searchValue = getDetailKey(searchWord);
  return statInfos.filter(({ name }) => getDetailKey(name).includes(searchValue));
};

const filterStatByDataTypes = (statInfos: StatInfo[], dataTypes: StatDataType[]) => {
  return statInfos.filter(({ dataType: statDataTypes }) => {
    const visibleDataTypes = statDataTypes.filter((type) => !HIDDEN_STAT_DATA_TYPES.includes(type));
    return (
      visibleDataTypes.length > 0 &&
      visibleDataTypes.some((statDataType) => dataTypes.includes(statDataType))
    );
  });
};

export const filterStat = (
  from: Map<ParentCategory, Map<ChildCategory, StatInfo[]>>,
  filterInfo: {
    searchWord: string;
    dataTypes: StatDataType[];
    availableStats?: string[];
  },
) => {
  const filtered = new Map();
  const { availableStats } = filterInfo;
  from.forEach((statInfoByCategory, parentCategory) => {
    statInfoByCategory.forEach((statInfos, childCategory) => {
      let filteredStatInfos = statInfos;
      filteredStatInfos = filterStatBySearchWord(filteredStatInfos, filterInfo.searchWord);
      filteredStatInfos = filterStatByDataTypes(filteredStatInfos, filterInfo.dataTypes);
      if (!filtered.get(parentCategory)) {
        filtered.set(parentCategory, new Map());
      }
      if (availableStats && availableStats.length > 0) {
        filteredStatInfos = filteredStatInfos.filter((statInfo) =>
          availableStats.includes(statInfo.id),
        );
      }
      filtered.get(parentCategory).set(childCategory, filteredStatInfos);
    });
  });

  return filtered;
};

export const isCategoryHidden = (parentCategory: string, hiddenRegExp: RegExp): boolean => {
  return hiddenRegExp.test(parentCategory);
};

export const filterCategoryList = (
  categoryList: Map<ChildCategory, StatInfo[]>,
  widgetChartType: WidgetChartType,
) => {
  let filteredCategoryList = new Map<ChildCategory, StatInfo[]>();

  if (Object.keys(validStatByChartType).includes(widgetChartType)) {
    categoryList.forEach((statKey, childCategory) => {
      const filteredStatInfoList = statKey.filter((statInfo) =>
        validStatByChartType[widgetChartType]?.includes(statInfo.id),
      );
      if (filteredStatInfoList.length > 0) {
        filteredCategoryList.set(childCategory, filteredStatInfoList);
      }
    });
  } else {
    filteredCategoryList = categoryList;
  }

  return filteredCategoryList;
};

const isChildCategoryVisible = (
  name: string,
  parentCategory: string,
  hiddenRegExp: {
    use: boolean;
    child: RegExp;
    allowedChildCategories?: string[];
  },
): boolean => {
  // allowedChildCategories가 있으면 그것을 우선 사용
  if (hiddenRegExp.use && hiddenRegExp.allowedChildCategories) {
    return hiddenRegExp.allowedChildCategories.includes(name);
  }

  // 기존 로직 유지
  if (hiddenRegExp.use && hiddenRegExp.child.test(name)) {
    if (!hiddenRegExp.child.test(parentCategory) && name === 'common') {
      return true;
    }
    return false;
  }
  return true;
};

export const mapAndSortChildCategories = (
  filteredCategoryList: Map<ChildCategory, StatInfo[]>,
  parentCategory: string,
  hiddenRegExp: {
    use: boolean;
    child: RegExp;
    allowedChildCategories?: string[];
  },
) => {
  const list = Array.from(filteredCategoryList)
    .map(([childCategory, statInfo]) => {
      return {
        name: mapChildCategoryToExact({ parentCategory, childCategory }),
        value: childCategory,
        count: statInfo.length,
      };
    })
    .sort((a, b) => sortCategory(a.name, b.name));
  const filteredList = list.filter(({ name }) =>
    isChildCategoryVisible(name, parentCategory, hiddenRegExp),
  );
  if (filteredList.length > 0 && !filteredList.find(({ name }) => name === 'common')) {
    const commonCategory = list.find(({ name }) => name === 'common');
    if (commonCategory) {
      return [commonCategory, ...filteredList];
    }
  }
  return filteredList;
};
